import { useCustomerStore, CUSTOMER_STORE_PERSIST_KEY } from "@/store";
import { signOut } from "@/lib/auth";
import { resolveSiblingAuthWrite } from "@njbsoft/store";

/**
 * Cross-tab session convergence OUTSIDE refresh moments. `storage` fires only
 * in tabs that did not write, so this is exactly "a sibling changed the
 * persisted session". The decision is `resolveSiblingAuthWrite`, shared by all
 * four portals so one rule governs every tab; this module supplies the store
 * and the portal-specific teardown. An adopted pair patches EXACTLY the token
 * triple and its stamp — never a blind rehydrate, because the active account view filter and the MFA challenge
 * state have their own lifecycle semantics and are not this module's to move (a
 * sibling switching properties must not move this tab's view).
 *
 * The two guarded arms, decided deliberately:
 * - This tab holds no live session (`refreshToken` null): do nothing. The
 *   module's job is keeping LIVE sessions converged; a sibling's sign-IN is
 *   never adopted into a tab parked on the login screen, which would mark it
 *   authenticated under a user object it never loaded.
 * - The persisted session is gone (a sibling signed out, clearing the bag):
 *   funnel through `signOut({ revoke: false })` — the portal's ONE teardown,
 *   which clears the active context and query cache before hard-navigating,
 *   never a partial null-patch that would leave the view filter behind. Revoke
 *   is FALSE for the same reason the auth-failure callbacks pass false: the
 *   sibling's sign-out already revoked this family server-side, and re-POSTing
 *   a logout for a token that is already gone buys nothing.
 *
 * Call once at module scope in `main.tsx`. Returns the unsubscribe for tests.
 */
export function setupSessionSync(): () => void {
  const onStorage = (event: StorageEvent): void => {
    if (event.key !== CUSTOMER_STORE_PERSIST_KEY) return;
    const state = useCustomerStore.getState();
    const decision = resolveSiblingAuthWrite(event.newValue, state);
    if (decision.action === "ignore") return;
    if (decision.action === "clear") {
      signOut({ revoke: false });
      return;
    }
    // Verbatim, stamp included: the adopted pair keeps the moment the
    // sibling minted it, so this tab's next refresh compares against the
    // rotation that actually happened rather than the moment it heard.
    state.adoptTokens(decision.tokens);
  };
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener("storage", onStorage);
  };
}
